import type {
  BoneAssignmentDiagramConnection,
  BoneAssignmentDiagramNode,
} from './types';
import {
  buildHandDiagram,
  HAND_DIAGRAM_LAYOUT,
  HAND_TEMPLATE_ROLE_NAME_MAP,
} from './handDiagramTemplates';

export type AdditionalChainSectionKey = 'limbs' | 'appendages';
export type AdditionalChainSubsectionKey = 'fingers';

export interface AdditionalChainSectionDefinition {
  key: AdditionalChainSectionKey;
  label: string;
  subsectionDefinitions?: readonly AdditionalChainSubsectionDefinition[];
}

export interface AdditionalChainSubsectionDefinition {
  key: AdditionalChainSubsectionKey;
  label: string;
}

export interface AdditionalChainDiagramTemplate {
  key: string;
  minHeightPx: number;
  maxWidthPx: number;
  nodes: readonly BoneAssignmentDiagramNode[];
  connections: readonly BoneAssignmentDiagramConnection[];
}

export interface AdditionalChainFamilyMemberDefinition {
  key: string;
  baseChainName: string;
  templateRolePath: readonly string[];
}

export interface AdditionalChainFamilyDefinition {
  key: string;
  label: string;
  sectionKey: AdditionalChainSectionKey;
  subsectionKey?: AdditionalChainSubsectionKey;
  templateKey: keyof typeof ADDITIONAL_CHAIN_DIAGRAM_TEMPLATES;
  instanceSourceBaseChainName?: string;
  includeBaseInstanceInAdditionalSection: boolean;
  maxInstanceIndex: number | null;
  addMemberKey: string | null;
  members: readonly AdditionalChainFamilyMemberDefinition[];
}

const LEFT_ARM_TEMPLATE_NODES: readonly BoneAssignmentDiagramNode[] = [
  { roleName: 'clavicle', shortLabel: 'Clav', x: 24, y: 28, size: 'xs' },
  { roleName: 'upper', shortLabel: 'UArm', x: 40, y: 24, size: 'sm' },
  { roleName: 'lower', shortLabel: 'LArm', x: 55, y: 39, size: 'sm' },
  { roleName: 'hand', shortLabel: 'Hand', x: 63, y: 60, size: 'sm' },
] as const;

const ARM_TEMPLATE_CONNECTIONS: readonly BoneAssignmentDiagramConnection[] = [
  { from: 'clavicle', to: 'upper' },
  { from: 'upper', to: 'lower' },
  { from: 'lower', to: 'hand' },
] as const;

const TAIL_TEMPLATE_NODES: readonly BoneAssignmentDiagramNode[] = [
  { roleName: 'base', shortLabel: 'Base', x: 22, y: 12, size: 'sm' },
  { roleName: 'curveA', shortLabel: '1', x: 26, y: 30, size: 'xs' },
  { roleName: 'curveB', shortLabel: '2', x: 34, y: 50, size: 'xs' },
  { roleName: 'curveC', shortLabel: '3', x: 47, y: 71, size: 'xs' },
  { roleName: 'tip', shortLabel: 'Tip', x: 64, y: 90, size: 'sm' },
] as const;

const TAIL_TEMPLATE_CONNECTIONS: readonly BoneAssignmentDiagramConnection[] = [
  { from: 'base', to: 'curveA' },
  { from: 'curveA', to: 'curveB' },
  { from: 'curveB', to: 'curveC' },
  { from: 'curveC', to: 'tip' },
] as const;

const TENTACLE_TEMPLATE_NODES: readonly BoneAssignmentDiagramNode[] = [
  { roleName: 'base', shortLabel: 'Base', x: 48, y: 16, size: 'sm' },
  { roleName: 'curveA', shortLabel: '1', x: 62, y: 34, size: 'xs' },
  { roleName: 'curveB', shortLabel: '2', x: 44, y: 52, size: 'xs' },
  { roleName: 'curveC', shortLabel: '3', x: 60, y: 72, size: 'xs' },
  { roleName: 'tip', shortLabel: 'Tip', x: 42, y: 88, size: 'sm' },
] as const;

const TENTACLE_TEMPLATE_CONNECTIONS: readonly BoneAssignmentDiagramConnection[] = [
  { from: 'base', to: 'curveA' },
  { from: 'curveA', to: 'curveB' },
  { from: 'curveB', to: 'curveC' },
  { from: 'curveC', to: 'tip' },
] as const;

const ANTENNAE_TEMPLATE_NODES: readonly BoneAssignmentDiagramNode[] = [
  { roleName: 'base', shortLabel: 'Base', x: 50, y: 18, size: 'sm' },
  { roleName: 'mid', shortLabel: 'Mid', x: 50, y: 50, size: 'xs' },
  { roleName: 'tip', shortLabel: 'Tip', x: 50, y: 82, size: 'sm' },
] as const;

const ANTENNAE_TEMPLATE_CONNECTIONS: readonly BoneAssignmentDiagramConnection[] = [
  { from: 'base', to: 'mid' },
  { from: 'mid', to: 'tip' },
] as const;

function mirrorNodes(
  nodes: readonly BoneAssignmentDiagramNode[],
): readonly BoneAssignmentDiagramNode[] {
  return nodes.map((node) => ({
    ...node,
    x: 100 - node.x,
  }));
}

export const ADDITIONAL_CHAIN_SECTION_DEFINITIONS: readonly AdditionalChainSectionDefinition[] =
  [
    {
      key: 'limbs',
      label: 'Additional Limbs',
      subsectionDefinitions: [{ key: 'fingers', label: 'Additional Fingers' }],
    },
    { key: 'appendages', label: 'Appendages' },
  ] as const;

const LEFT_HAND_TEMPLATE_DIAGRAM = buildHandDiagram({
  key: 'additional-template-left-hand',
  label: 'Left',
  side: 'left',
  roleNameByTemplateRole: HAND_TEMPLATE_ROLE_NAME_MAP,
});

const RIGHT_HAND_TEMPLATE_DIAGRAM = buildHandDiagram({
  key: 'additional-template-right-hand',
  label: 'Right',
  side: 'right',
  roleNameByTemplateRole: HAND_TEMPLATE_ROLE_NAME_MAP,
});

export const ADDITIONAL_CHAIN_DIAGRAM_TEMPLATES = {
  leftArm: {
    key: 'left-arm',
    minHeightPx: 152,
    maxWidthPx: 200,
    nodes: LEFT_ARM_TEMPLATE_NODES,
    connections: ARM_TEMPLATE_CONNECTIONS,
  },
  rightArm: {
    key: 'right-arm',
    minHeightPx: 152,
    maxWidthPx: 200,
    nodes: mirrorNodes(LEFT_ARM_TEMPLATE_NODES),
    connections: ARM_TEMPLATE_CONNECTIONS,
  },
  tail: {
    key: 'tail',
    minHeightPx: 152,
    maxWidthPx: 180,
    nodes: TAIL_TEMPLATE_NODES,
    connections: TAIL_TEMPLATE_CONNECTIONS,
  },
  tentacle: {
    key: 'tentacle',
    minHeightPx: 152,
    maxWidthPx: 180,
    nodes: TENTACLE_TEMPLATE_NODES,
    connections: TENTACLE_TEMPLATE_CONNECTIONS,
  },
  antennae: {
    key: 'antennae',
    minHeightPx: 152,
    maxWidthPx: 180,
    nodes: ANTENNAE_TEMPLATE_NODES,
    connections: ANTENNAE_TEMPLATE_CONNECTIONS,
  },
  leftHand: {
    key: 'left-hand',
    minHeightPx: HAND_DIAGRAM_LAYOUT.minHeightPx,
    maxWidthPx: HAND_DIAGRAM_LAYOUT.maxWidthPx,
    nodes: LEFT_HAND_TEMPLATE_DIAGRAM.nodes,
    connections: LEFT_HAND_TEMPLATE_DIAGRAM.connections,
  },
  rightHand: {
    key: 'right-hand',
    minHeightPx: HAND_DIAGRAM_LAYOUT.minHeightPx,
    maxWidthPx: HAND_DIAGRAM_LAYOUT.maxWidthPx,
    nodes: RIGHT_HAND_TEMPLATE_DIAGRAM.nodes,
    connections: RIGHT_HAND_TEMPLATE_DIAGRAM.connections,
  },
} as const satisfies Record<string, AdditionalChainDiagramTemplate>;

export const ADDITIONAL_CHAIN_FAMILY_DEFINITIONS: readonly AdditionalChainFamilyDefinition[] =
  [
    {
      key: 'leftArm',
      label: 'Left Arm',
      sectionKey: 'limbs',
      templateKey: 'leftArm',
      includeBaseInstanceInAdditionalSection: false,
      maxInstanceIndex: 5,
      addMemberKey: 'arm',
      members: [
        {
          key: 'arm',
          baseChainName: 'LeftArm',
          templateRolePath: ['upper', 'lower', 'hand'],
        },
        {
          key: 'clavicle',
          baseChainName: 'LeftClavicle',
          templateRolePath: ['clavicle'],
        },
      ],
    },
    {
      key: 'rightArm',
      label: 'Right Arm',
      sectionKey: 'limbs',
      templateKey: 'rightArm',
      includeBaseInstanceInAdditionalSection: false,
      maxInstanceIndex: 5,
      addMemberKey: 'arm',
      members: [
        {
          key: 'arm',
          baseChainName: 'RightArm',
          templateRolePath: ['upper', 'lower', 'hand'],
        },
        {
          key: 'clavicle',
          baseChainName: 'RightClavicle',
          templateRolePath: ['clavicle'],
        },
      ],
    },
    {
      key: 'tail',
      label: 'Tail',
      sectionKey: 'appendages',
      templateKey: 'tail',
      includeBaseInstanceInAdditionalSection: true,
      maxInstanceIndex: null,
      addMemberKey: 'chain',
      members: [
        {
          key: 'chain',
          baseChainName: 'Tail',
          templateRolePath: ['base', 'curveA', 'curveB', 'curveC', 'tip'],
        },
      ],
    },
    {
      key: 'tentacle',
      label: 'Tentacle',
      sectionKey: 'appendages',
      templateKey: 'tentacle',
      includeBaseInstanceInAdditionalSection: true,
      maxInstanceIndex: null,
      addMemberKey: 'chain',
      members: [
        {
          key: 'chain',
          baseChainName: 'Tentacle',
          templateRolePath: ['base', 'curveA', 'curveB', 'curveC', 'tip'],
        },
      ],
    },
    {
      key: 'antennae',
      label: 'Antennae',
      sectionKey: 'appendages',
      templateKey: 'antennae',
      includeBaseInstanceInAdditionalSection: true,
      maxInstanceIndex: null,
      addMemberKey: 'chain',
      members: [
        {
          key: 'chain',
          baseChainName: 'Antennae',
          templateRolePath: ['base', 'mid', 'tip'],
        },
      ],
    },
    {
      key: 'leftHandFingers',
      label: 'Left Hand',
      sectionKey: 'limbs',
      subsectionKey: 'fingers',
      templateKey: 'leftHand',
      instanceSourceBaseChainName: 'LeftArm',
      includeBaseInstanceInAdditionalSection: false,
      maxInstanceIndex: 5,
      addMemberKey: null,
      members: [
        {
          key: 'thumb',
          baseChainName: 'LeftThumb',
          templateRolePath: ['thumb1', 'thumb2', 'thumb3'],
        },
        {
          key: 'indexMetacarpal',
          baseChainName: 'LeftIndexMetacarpal',
          templateRolePath: ['indexMetacarpal'],
        },
        {
          key: 'index',
          baseChainName: 'LeftIndex',
          templateRolePath: ['index1', 'index2', 'index3'],
        },
        {
          key: 'middleMetacarpal',
          baseChainName: 'LeftMiddleMetacarpal',
          templateRolePath: ['middleMetacarpal'],
        },
        {
          key: 'middle',
          baseChainName: 'LeftMiddle',
          templateRolePath: ['middle1', 'middle2', 'middle3'],
        },
        {
          key: 'ringMetacarpal',
          baseChainName: 'LeftRingMetacarpal',
          templateRolePath: ['ringMetacarpal'],
        },
        {
          key: 'ring',
          baseChainName: 'LeftRing',
          templateRolePath: ['ring1', 'ring2', 'ring3'],
        },
        {
          key: 'pinkyMetacarpal',
          baseChainName: 'LeftPinkyMetacarpal',
          templateRolePath: ['pinkyMetacarpal'],
        },
        {
          key: 'pinky',
          baseChainName: 'LeftPinky',
          templateRolePath: ['pinky1', 'pinky2', 'pinky3'],
        },
      ],
    },
    {
      key: 'rightHandFingers',
      label: 'Right Hand',
      sectionKey: 'limbs',
      subsectionKey: 'fingers',
      templateKey: 'rightHand',
      instanceSourceBaseChainName: 'RightArm',
      includeBaseInstanceInAdditionalSection: false,
      maxInstanceIndex: 5,
      addMemberKey: null,
      members: [
        {
          key: 'thumb',
          baseChainName: 'RightThumb',
          templateRolePath: ['thumb1', 'thumb2', 'thumb3'],
        },
        {
          key: 'indexMetacarpal',
          baseChainName: 'RightIndexMetacarpal',
          templateRolePath: ['indexMetacarpal'],
        },
        {
          key: 'index',
          baseChainName: 'RightIndex',
          templateRolePath: ['index1', 'index2', 'index3'],
        },
        {
          key: 'middleMetacarpal',
          baseChainName: 'RightMiddleMetacarpal',
          templateRolePath: ['middleMetacarpal'],
        },
        {
          key: 'middle',
          baseChainName: 'RightMiddle',
          templateRolePath: ['middle1', 'middle2', 'middle3'],
        },
        {
          key: 'ringMetacarpal',
          baseChainName: 'RightRingMetacarpal',
          templateRolePath: ['ringMetacarpal'],
        },
        {
          key: 'ring',
          baseChainName: 'RightRing',
          templateRolePath: ['ring1', 'ring2', 'ring3'],
        },
        {
          key: 'pinkyMetacarpal',
          baseChainName: 'RightPinkyMetacarpal',
          templateRolePath: ['pinkyMetacarpal'],
        },
        {
          key: 'pinky',
          baseChainName: 'RightPinky',
          templateRolePath: ['pinky1', 'pinky2', 'pinky3'],
        },
      ],
    },
  ] as const;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function getAdditionalChainInstanceIndex(
  baseChainName: string,
  chainName: string,
): number | null {
  const match = chainName.match(
    new RegExp(`^${escapeRegExp(baseChainName)}(?:_(\\d+))?$`),
  );
  if (!match) return null;
  if (!match[1]) return 1;
  const parsedIndex = Number.parseInt(match[1], 10);
  return Number.isFinite(parsedIndex) && parsedIndex >= 1 ? parsedIndex : null;
}

export function formatAdditionalChainName(
  baseChainName: string,
  instanceIndex: number,
): string {
  return instanceIndex <= 1 ? baseChainName : `${baseChainName}_${instanceIndex}`;
}

export function getAdditionalChainMemberChainName(
  member: AdditionalChainFamilyMemberDefinition,
  instanceIndex: number,
): string {
  return formatAdditionalChainName(member.baseChainName, instanceIndex);
}
